import { Link, useLocation, useNavigate } from 'react-router-dom';
import { markInAppNavigation } from 'lib/api';
import { navigateInApp } from 'lib/navigation';
import { BookOpen, Settings, Volume2, VolumeX, Wallet } from 'lucide-react';
import { useAuth } from 'context/AuthProvider';
import { useSound } from 'context/SoundProvider';
import { getPlayableBalance, getFreeBalance } from 'lib/balance';
import { currencyLocale } from 'lib/currency-format';
import HeaderPlayerName from 'components/HeaderPlayerName';
import GameLogo from 'components/GameLogo';
import { useCurrency } from 'hooks/useCurrency';
import { rulesPathForVariant } from 'lib/variant-rules';

interface ProviderHeaderProps {
  homeLinkTo?: string;
  /** When set, wallet tap uses this instead of navigating to /transactions (e.g. home login gate). */
  onWalletClick?: () => void;
}

export default function ProviderHeader({ homeLinkTo = '/home', onWalletClick }: ProviderHeaderProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { muted, toggleMute } = useSound();
  const { symbol, code } = useCurrency();
  const showRulesLink = location.pathname.startsWith('/lobby');
  const lobbyVariant = new URLSearchParams(location.search).get('variant') || 'Classic';
  const rulesPath = rulesPathForVariant(lobbyVariant);

  const mainAmount = getPlayableBalance(user);
  const walletBalance = getFreeBalance(user);
  const tableStack = Math.max(0, Math.floor(Number(user?.tableStack) || 0));
  const showTableBreakdown = tableStack > 0 && !user?.consumerWallet;

  const formatAmountNumber = (amount: number) =>
    Math.max(0, Number(amount) || 0).toLocaleString(currencyLocale(code), {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

  const handleWalletClick = () => {
    if (onWalletClick) {
      onWalletClick();
      return;
    }
    navigateInApp(navigate, '/transactions');
  };

  return (
    <header className="provider-header relative z-20 shrink-0 px-4 py-4 sm:px-8 lg:px-10">
      <div className="provider-header-inner mx-auto max-w-[1400px]">
        <div className="provider-header-left">
          <Link
            to={homeLinkTo}
            onClick={() => markInAppNavigation()}
            className="flex shrink-0 items-end gap-3"
          >
            <GameLogo />
          </Link>
        </div>

        <div className="provider-header-center">
          <button
            type="button"
            className="provider-wallet provider-wallet-btn"
            onClick={handleWalletClick}
            aria-label="View transaction history"
          >
            <Wallet size={22} className="provider-wallet-icon shrink-0 text-tp-gold" />
            <div className="provider-wallet-body min-w-0">
              <p className="provider-wallet-label">Available Balance</p>
              <div className="provider-wallet-amount-row">
                <span className="provider-currency-badge" aria-hidden>
                  {symbol}
                </span>
                <span className="provider-wallet-amount tabular-nums">{formatAmountNumber(mainAmount)}</span>
              </div>
              {showTableBreakdown && (
                <p className="provider-wallet-sub truncate">
                  Wallet {formatAmountNumber(walletBalance)} · On table {formatAmountNumber(tableStack)}
                </p>
              )}
            </div>
          </button>
        </div>

        <div className="provider-header-right">
          {user && (
            <HeaderPlayerName username={user.username} forceShowName className="provider-header-player" />
          )}
          {showRulesLink && (
            <Link to={rulesPath} className="provider-header-btn">
              <BookOpen size={22} className="text-tp-gold" />
              <span>Rules</span>
            </Link>
          )}
          <button type="button" className="provider-header-btn" onClick={toggleMute} aria-label="Sound">
            {muted ? (
              <VolumeX size={22} className="text-tp-gold" />
            ) : (
              <Volume2 size={22} className="text-tp-gold" />
            )}
            <span>Sound</span>
          </button>
          <Link to="/settings" className="provider-header-btn">
            <Settings size={22} className="text-tp-gold" />
            <span>Settings</span>
          </Link>
        </div>
      </div>
    </header>
  );
}
